import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { db, storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Plus, Edit2, Trash2, Eye, Clock, MousePointer, TrendingUp, AlertCircle, CheckCircle, Upload, Image as ImageIcon, X } from 'lucide-react';

function AdManager({ user }) {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);

  const loadAds = useCallback(async () => {
    try {
      setLoading(true);
      const adsRef = collection(db, 'advertisements');
      const q = query(adsRef, where('advertiserId', '==', user.uid));
      const snapshot = await getDocs(q);
      const adsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAds(adsList);
    } catch (error) {
      console.error('광고 로딩 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [user.uid]);

  useEffect(() => { loadAds(); }, [loadAds]);

  const handleDelete = async (adId) => {
    if (!window.confirm('정말 이 광고를 삭제하시겠습니까?')) return;
    try {
      await deleteDoc(doc(db, 'advertisements', adId));
      alert('광고가 삭제되었습니다.');
      loadAds();
    } catch (error) {
      console.error('광고 삭제 실패:', error);
      alert('광고 삭제에 실패했습니다.');
    }
  };

  const handleStatusToggle = async (ad) => {
    try {
      const newStatus = ad.status === 'active' ? 'paused' : 'active';
      await updateDoc(doc(db, 'advertisements', ad.id), { status: newStatus });
      alert(`광고가 ${newStatus === 'active' ? '활성화' : '일시정지'}되었습니다.`);
      loadAds();
    } catch (error) {
      console.error('상태 변경 실패:', error);
      alert('상태 변경에 실패했습니다.');
    }
  };

  const totalStats = ads.reduce((acc, ad) => ({
    impressions: acc.impressions + (ad.impressions || 0),
    clicks: acc.clicks + (ad.clicks || 0),
    active: acc.active + (ad.status === 'active' ? 1 : 0),
  }), { impressions: 0, clicks: 0, active: 0 });

  const avgCTR = totalStats.impressions > 0 ? ((totalStats.clicks / totalStats.impressions) * 100).toFixed(2) : 0;

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>로딩 중...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.headerIconBox}>
            <ImageIcon size={20} color="#ffffff" />
          </div>
          <span style={styles.headerTitle}>광고 관리</span>
        </div>
        <button onClick={() => setShowCreateModal(true)} style={styles.createButton}>
          <Plus size={18} />
          <span>새 광고</span>
        </button>
      </div>

      {/* 통계 */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statIconBox}><Eye size={20} color="#6366f1" /></div>
          <div style={styles.statInfo}>
            <span style={styles.statValue}>{totalStats.impressions.toLocaleString()}</span>
            <span style={styles.statLabel}>총 노출</span>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIconBox, backgroundColor: '#dbeafe' }}><MousePointer size={20} color="#3b82f6" /></div>
          <div style={styles.statInfo}>
            <span style={styles.statValue}>{totalStats.clicks.toLocaleString()}</span>
            <span style={styles.statLabel}>총 클릭</span>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIconBox, backgroundColor: '#d1fae5' }}><TrendingUp size={20} color="#10b981" /></div>
          <div style={styles.statInfo}>
            <span style={styles.statValue}>{avgCTR}%</span>
            <span style={styles.statLabel}>평균 CTR</span>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIconBox, backgroundColor: '#fef3c7' }}><CheckCircle size={20} color="#f59e0b" /></div>
          <div style={styles.statInfo}>
            <span style={styles.statValue}>{totalStats.active}</span>
            <span style={styles.statLabel}>활성 광고</span>
          </div>
        </div>
      </div>

      {/* 광고 목록 */}
      <div style={styles.listContainer}>
        {ads.length === 0 ? (
          <div style={styles.emptyContainer}>
            <div style={styles.emptyIconBox}>
              <ImageIcon size={40} color="#6366f1" />
            </div>
            <p style={styles.emptyText}>등록된 광고가 없습니다</p>
            <p style={styles.emptySubtext}>첫 광고를 만들어보세요!</p>
          </div>
        ) : (
          <div style={styles.adsList}>
            {ads.map(ad => (
              <div key={ad.id} style={styles.adCard}>
                {/* 카드 헤더 */}
                <div style={styles.cardHeader}>
                  <div style={styles.tierBadge}>
                    <span style={styles.tierBadgeText}>
                      {ad.tier === 'premium' ? '프리미엄' : ad.tier === 'standard' ? '스탠다드' : '베이직'}
                    </span>
                  </div>
                  {ad.status === 'active' ? (
                    <div style={styles.statusActive}><CheckCircle size={14} /><span>활성</span></div>
                  ) : ad.status === 'paused' ? (
                    <div style={styles.statusPaused}><AlertCircle size={14} /><span>일시정지</span></div>
                  ) : (
                    <div style={styles.statusPending}><Clock size={14} /><span>검토중</span></div>
                  )}
                </div>

                {/* 이미지 */}
                {ad.imageUrl && (
                  <div style={styles.adImageContainer}>
                    <img src={ad.imageUrl} alt={ad.title} style={styles.adImage} />
                  </div>
                )}

                {/* 제목 & 설명 */}
                <h3 style={styles.adTitle}>{ad.title}</h3>
                <p style={styles.adDescription}>{ad.description}</p>

                {/* 통계 */}
                <div style={styles.cardStats}>
                  <div style={styles.cardStatItem}>
                    <Eye size={14} color="#64748b" />
                    <span>{(ad.impressions || 0).toLocaleString()}</span>
                  </div>
                  <div style={styles.cardStatItem}>
                    <MousePointer size={14} color="#64748b" />
                    <span>{(ad.clicks || 0).toLocaleString()}</span>
                  </div>
                  <div style={styles.cardStatItem}>
                    <TrendingUp size={14} color="#64748b" />
                    <span>{ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(2) : 0}%</span>
                  </div>
                </div>

                {/* 메타 정보 */}
                <div style={styles.cardMeta}>
                  <span style={styles.metaText}>
                    {ad.position === 'top-banner' ? '상단 배너' : ad.position === 'sidebar' ? '사이드바' : '하단'}
                  </span>
                  <span style={styles.metaDot}>•</span>
                  <span style={styles.metaText}>
                    {ad.startDate?.toDate().toLocaleDateString()} ~ {ad.endDate?.toDate().toLocaleDateString()}
                  </span>
                </div>

                {/* 액션 버튼 */}
                <div style={styles.cardActions}>
                  <button onClick={() => handleStatusToggle(ad)} style={styles.actionBtn}>
                    {ad.status === 'active' ? '일시정지' : '활성화'}
                  </button>
                  <button onClick={() => setEditingAd(ad)} style={styles.actionBtn}>
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => handleDelete(ad.id)} style={styles.actionBtnDanger}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 모달 */}
      {(showCreateModal || editingAd) && (
        <AdFormModal
          ad={editingAd}
          onClose={() => { setShowCreateModal(false); setEditingAd(null); }}
          onSave={() => { setShowCreateModal(false); setEditingAd(null); loadAds(); }}
          user={user}
        />
      )}

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function AdFormModal({ ad, onClose, onSave, user }) {
  const [formData, setFormData] = useState({
    title: ad?.title || '',
    description: ad?.description || '',
    url: ad?.url || '',
    tier: ad?.tier || 'basic',
    position: ad?.position || 'top-banner',
    startDate: ad?.startDate?.toDate().toISOString().split('T')[0] || '',
    endDate: ad?.endDate?.toDate().toISOString().split('T')[0] || '',
    targetBusinessType: ad?.targeting?.businessType || 'all',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(ad?.imageUrl || null);
  const [uploading, setUploading] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      let imageUrl = ad?.imageUrl;

      if (imageFile) {
        const storageRef = ref(storage, `advertisements/${Date.now()}_${imageFile.name}`);
        await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(storageRef);
      }

      const adData = {
        title: formData.title,
        description: formData.description,
        url: formData.url,
        imageUrl,
        tier: formData.tier,
        position: formData.position,
        startDate: Timestamp.fromDate(new Date(formData.startDate)),
        endDate: Timestamp.fromDate(new Date(formData.endDate)),
        targeting: { businessType: formData.targetBusinessType },
        advertiserId: user.uid,
        advertiserInfo: { businessName: user.businessName, email: user.email },
        status: 'pending',
        impressions: ad?.impressions || 0,
        clicks: ad?.clicks || 0,
        createdAt: ad?.createdAt || Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      if (ad) {
        await updateDoc(doc(db, 'advertisements', ad.id), adData);
        alert('광고가 수정되었습니다. 관리자 승인을 기다려주세요.');
      } else {
        await addDoc(collection(db, 'advertisements'), adData);
        alert('광고가 등록되었습니다. 관리자 승인을 기다려주세요.');
      }
      onSave();
    } catch (error) {
      console.error('광고 저장 실패:', error);
      alert('광고 저장에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <span style={styles.modalTitle}>{ad ? '광고 수정' : '새 광고 만들기'}</span>
          <button onClick={onClose} style={styles.modalCloseBtn}><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} style={styles.modalBody}>
          {/* 이미지 업로드 */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>광고 이미지</label>
            <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} id="image-upload" />
            <label htmlFor="image-upload" style={styles.uploadBtn}>
              <Upload size={18} />
              <span>이미지 선택</span>
            </label>
            {imagePreview && <img src={imagePreview} alt="Preview" style={styles.imagePreview} />}
          </div>

          {/* 제목 */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>광고 제목 *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              style={styles.formInput}
              required
            />
          </div>

          {/* 설명 */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>설명</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              style={styles.formTextarea}
              rows={3}
            />
          </div>

          {/* URL */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>링크 URL *</label>
            <input
              type="url"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              style={styles.formInput}
              placeholder="https://"
              required
            />
          </div>

          {/* 등급 & 위치 */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>광고 등급 *</label>
              <select value={formData.tier} onChange={(e) => setFormData({ ...formData, tier: e.target.value })} style={styles.formSelect}>
                <option value="basic">베이직 (월 20만원)</option>
                <option value="standard">스탠다드 (월 50만원)</option>
                <option value="premium">프리미엄 (월 100만원)</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>광고 위치 *</label>
              <select value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} style={styles.formSelect}>
                <option value="top-banner">상단 배너</option>
                <option value="sidebar">사이드바</option>
                <option value="footer">하단</option>
              </select>
            </div>
          </div>

          {/* 타겟 */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>타겟 고객</label>
            <select value={formData.targetBusinessType} onChange={(e) => setFormData({ ...formData, targetBusinessType: e.target.value })} style={styles.formSelect}>
              <option value="all">전체</option>
              <option value="clinic">치과만</option>
              <option value="lab">기공소만</option>
            </select>
          </div>

          {/* 기간 */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>시작일 *</label>
              <input type="date" value={formData.startDate} onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} style={styles.formInput} required />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>종료일 *</label>
              <input type="date" value={formData.endDate} onChange={(e) => setFormData({ ...formData, endDate: e.target.value })} style={styles.formInput} required />
            </div>
          </div>

          {/* 버튼 */}
          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>취소</button>
            <button type="submit" style={styles.submitBtn} disabled={uploading}>
              {uploading ? '저장 중...' : ad ? '수정' : '등록'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f1f5f9' },

  // 헤더
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', backgroundColor: '#6366f1' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  headerIconBox: { width: '36px', height: '36px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  headerTitle: { fontSize: '20px', fontWeight: '700', color: '#ffffff' },
  createButton: { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', backgroundColor: 'rgba(255,255,255,0.2)', color: '#ffffff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },

  // 통계
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', padding: '14px' },
  statCard: { display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', backgroundColor: '#ffffff', borderRadius: '14px' },
  statIconBox: { width: '40px', height: '40px', borderRadius: '10px', backgroundColor: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  statInfo: { display: 'flex', flexDirection: 'column' },
  statValue: { fontSize: '18px', fontWeight: '700', color: '#0f172a' },
  statLabel: { fontSize: '12px', color: '#64748b', marginTop: '2px' },

  // 목록
  listContainer: { padding: '0 14px 14px' },
  adsList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  adCard: { backgroundColor: '#ffffff', borderRadius: '14px', padding: '14px' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  tierBadge: { padding: '5px 10px', backgroundColor: '#6366f1', borderRadius: '8px' },
  tierBadgeText: { fontSize: '11px', fontWeight: '700', color: '#ffffff' },
  statusActive: { display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', backgroundColor: '#d1fae5', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#059669' },
  statusPaused: { display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', backgroundColor: '#fee2e2', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#dc2626' },
  statusPending: { display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', backgroundColor: '#fef3c7', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#d97706' },
  adImageContainer: { marginBottom: '12px' },
  adImage: { width: '100%', height: '150px', objectFit: 'cover', borderRadius: '10px' },
  adTitle: { margin: '0 0 4px', fontSize: '17px', fontWeight: '700', color: '#0f172a' },
  adDescription: { margin: '0 0 12px', fontSize: '13px', color: '#64748b' },
  cardStats: { display: 'flex', gap: '16px', padding: '12px 0', borderTop: '1px solid #f1f5f9' },
  cardStatItem: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#64748b' },
  cardMeta: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' },
  metaText: { fontSize: '12px', color: '#94a3b8' },
  metaDot: { fontSize: '12px', color: '#94a3b8' },
  cardActions: { display: 'flex', gap: '8px' },
  actionBtn: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '10px', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '600', color: '#64748b', cursor: 'pointer' },
  actionBtnDanger: { width: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px', backgroundColor: '#fef2f2', border: 'none', borderRadius: '10px', color: '#ef4444', cursor: 'pointer' },

  // 빈 상태
  emptyContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px' },
  emptyIconBox: { width: '80px', height: '80px', borderRadius: '40px', backgroundColor: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' },
  emptyText: { fontSize: '16px', fontWeight: '600', color: '#374151', margin: 0 },
  emptySubtext: { fontSize: '14px', color: '#9ca3af', marginTop: '8px' },

  // 로딩
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: '16px' },
  spinner: { width: '40px', height: '40px', border: '4px solid #e2e8f0', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  loadingText: { fontSize: '14px', color: '#64748b' },

  // 모달
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 },
  modalContent: { width: '100%', maxWidth: '600px', maxHeight: '90vh', backgroundColor: '#ffffff', borderTopLeftRadius: '20px', borderTopRightRadius: '20px', overflow: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px', borderBottom: '1px solid #f1f5f9' },
  modalTitle: { fontSize: '17px', fontWeight: '700', color: '#0f172a' },
  modalCloseBtn: { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer' },
  modalBody: { padding: '18px' },
  modalFooter: { display: 'flex', gap: '10px', paddingTop: '18px', borderTop: '1px solid #f1f5f9' },

  // 폼
  formGroup: { marginBottom: '16px' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  formLabel: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' },
  formInput: { width: '100%', padding: '12px', fontSize: '14px', border: '2px solid #e2e8f0', borderRadius: '10px', outline: 'none', boxSizing: 'border-box' },
  formTextarea: { width: '100%', padding: '12px', fontSize: '14px', border: '2px solid #e2e8f0', borderRadius: '10px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' },
  formSelect: { width: '100%', padding: '12px', fontSize: '14px', border: '2px solid #e2e8f0', borderRadius: '10px', outline: 'none', backgroundColor: '#fff', boxSizing: 'border-box' },
  uploadBtn: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 20px', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', color: '#64748b', cursor: 'pointer' },
  imagePreview: { width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '10px', marginTop: '12px' },
  cancelBtn: { flex: 1, padding: '14px', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', color: '#64748b', cursor: 'pointer' },
  submitBtn: { flex: 1, padding: '14px', backgroundColor: '#6366f1', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', color: '#ffffff', cursor: 'pointer' },
};

export default AdManager;